import { Core } from "@faire/design-tokens";
import { IBrandViewV2Response } from "@faire/web-api/indigofair/data/IBrandViewV2Response";
import { HorizontalLine } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React from "react";

import type { IReviewSummary } from "@faire/retailer/lib/buildReviewSummary";

import { SIGN_UP_BUTTON_WIDTH_DESKTOP } from "./consts";
import { Rating } from "./Rating";
import { SignUpButton } from "./SignUpButton";
import { StoryModalButton } from "./StoryModalButton";
import * as styles from "./styles.css";

type Props = {
  brand: IBrandViewV2Response.ILeanBrand;
  reviewSummary: IReviewSummary | undefined;
  brandAboutSubheader: string | null;
};

export const Info: React.FC<Props> = ({
  brand,
  reviewSummary,
  brandAboutSubheader,
}) => {
  return (
    <div className={styles.container}>
      {brand.name ? (
        <>
          <Typography
            as="h1"
            variant={{
              mobileAndAbove: "pageHeaderSerifRegular",
              tabletAndAbove: "displaySSerifRegular",
            }}
            align={{
              mobileAndAbove: "center",
              tabletAndAbove: "left",
            }}
            data-test-id="productGridHeader"
          >
            <StrictLocalMsg
              defaultMessage="{brandCompanyName} wholesale products"
              description={{
                text: "Header text for logged-out brand page",
                img: "https://cdn.faire.com/fastly/e0f5295f3cbc9032c1461a5a49ac9cd6c4776e1a25290b4b93a91d28954991eb.png",
              }}
              values={{ brandCompanyName: brand.name }}
            />
          </Typography>
          <Spacer
            height={{
              mobileAndAbove: "1x",
              tabletAndAbove: "2x",
            }}
          />
          <Typography
            as="h2"
            variant={{
              mobileAndAbove: "paragraphSansRegular",
              tabletAndAbove: "subheadingSansRegular",
            }}
            align={{
              mobileAndAbove: "center",
              tabletAndAbove: "left",
            }}
            color={Core.text.subdued}
          >
            <StrictLocalMsg
              defaultMessage="Shop {brandCompanyName} and other independent brands on the Faire wholesale marketplace."
              description={{
                text: "Subheader text for logged-out brand page",
                img: "https://cdn.faire.com/fastly/837af0d0d251542ced3974518f1cf9dc3057e88007fa76e0b43050864a125a96.png",
              }}
              values={{ brandCompanyName: brand.name }}
            />
          </Typography>
        </>
      ) : null}
      <Spacer
        height={{
          mobileAndAbove: "3x",
          tabletAndAbove: "4x",
        }}
      />

      <SignUpButton brandToken={brand.token} />

      <Spacer
        height={{
          mobileAndAbove: "4x",
          tabletAndAbove: "5x",
        }}
      />

      <ResponsiveContentV2
        mobile={<HorizontalLine width="120px" />}
        tabletAndAbove={
          <HorizontalLine width={`${SIGN_UP_BUTTON_WIDTH_DESKTOP}px`} />
        }
      />

      <Spacer
        height={{
          mobileAndAbove: "4x",
          tabletAndAbove: "5x",
        }}
      />

      <ResponsiveContentV2
        tabletAndAbove={
          <>
            {reviewSummary ? <Rating reviewSummary={reviewSummary} /> : null}
            <Spacer height="2x" />

            {brand.description ? (
              <>
                <Typography color={Core.text.subdued} maxLines={3}>
                  {brand.description}
                </Typography>
                <Spacer height="2x" />
              </>
            ) : null}

            <StoryModalButton
              brand={brand}
              brandAboutSubheader={brandAboutSubheader}
            />
          </>
        }
      />
    </div>
  );
};
